import { extendTheme, type ThemeConfig } from "@chakra-ui/react"

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
}

const theme = extendTheme({
  config,
  colors: {
    // Core brand palettes (built around the CMYK midpoints)
    primary: {
      50:  "#EAFBFF",
      100: "#C8F2FF",
      200: "#A5E9FF",
      300: "#6FDEFF",
      400: "#38D3FF",
      500: "#0DD9FF", // Electric cyan-blue
      600: "#0CBFE3",
      700: "#0A9CB8",
      800: "#087A8E",
      900: "#075D6C",
    },
    purple: {
      50:  "#F2E9FF",
      100: "#E0C9FF",
      200: "#C79FFF",
      300: "#AD74FF",
      400: "#944DFF",
      500: "#8033FF", // Metallic purple
      600: "#6E2AD6",
      700: "#5B23B2",
      800: "#471B8E",
      900: "#33156B",
    },
    silver: {
      50:  "#F2F2F2",
      100: "#E6E6E6",
      200: "#D9D9D9",
      300: "#BFBFBF",
      400: "#A6A6A6",
      500: "#999999", // Metallic silver/grey (≈ K40)
      600: "#7F7F7F",
      700: "#666666",
      800: "#4D4D4D",
      900: "#333333",
    },

    // Single-value accent tokens for the glowing “X” edges
    blueGlow:   "#1ACCFF",
    purpleGlow: "#8C0CFF",
    orangeGlow: "#FF801A",

    // Convenience aliases if you want "brand" to equal the cyan
    brand: {
      50:  "#EAFBFF",
      100: "#C8F2FF",
      200: "#A5E9FF",
      300: "#6FDEFF",
      400: "#38D3FF",
      500: "#0DD9FF",
      600: "#0CBFE3",
      700: "#0A9CB8",
      800: "#087A8E",
      900: "#075D6C",
    },
  },

  fonts: {
    heading:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    body:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  },

  components: {
    Button: {
      defaultProps: {
        colorScheme: "primary",
        variant: "solid",
      },
      variants: {
        solid: {
          bg: "primary.600",
          color: "white",
          _hover: { bg: "primary.700" },
          _active: { bg: "primary.800" },
        },
        outline: {
          color: "primary.600",
          borderColor: "primary.600",
          _hover: { bg: "primary.50", borderColor: "primary.700", color: "primary.700" },
        },
        ghost: {
          color: "primary.600",
          _hover: { bg: "primary.50" },
        },
        // Optional fun variants that reference the glow accents
        blueGlow:   { bg: "blueGlow",   color: "white", _hover: { filter: "brightness(0.95)" } },
        purpleGlow: { bg: "purpleGlow", color: "white", _hover: { filter: "brightness(0.95)" } },
        orangeGlow: { bg: "orangeGlow", color: "black", _hover: { filter: "brightness(0.95)" } },
        brandGradient: {
          bgGradient: "linear(135deg, #c091cf, #7154b9)",
          color: "white",
          border: "none",
          px: "30px",
          py: "12px",
          fontSize: "16px",
          transition: "all 0.3s ease",
          overflow: "visible",
          boxShadow: "0 6px 18px rgba(113, 84, 185, 0.5)",

          // keep the same gradient in ALL states
          _hover: {
            bgGradient: "linear(135deg, #c091cf, #7154b9)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 18px rgba(113, 84, 185, 0.5)",
            filter: "brightness(0.9)",
          },
          _active: {
            bgGradient: "linear(135deg, #c091cf, #7154b9)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 18px rgba(113, 84, 185, 0.5)",
            filter: "brightness(0.8)",
          },
          _focusVisible: {
            bgGradient: "linear(135deg, #c091cf, #7154b9)",
            outline: "0",
            boxShadow:
              "0 0 0 2px rgba(255,255,255,0.7), 0 6px 18px rgba(113, 84, 185, 0.5)",
          },
          _disabled: {
            bgGradient: "linear(135deg, #c091cf, #7154b9)",
            boxShadow: "0 6px 18px rgba(113, 84, 185, 0.5)",
            opacity: 0.7,
            cursor: "not-allowed",
          },
          _loading: {
            // Chakra applies [data-loading]; keep gradient and freeze hover/active
            bgGradient: "linear(135deg, #c091cf, #7154b9)",
            color: "white",
            boxShadow: "0 6px 18px rgba(113, 84, 185, 0.5)",
            _hover: {
              bgGradient: "linear(135deg, #c091cf, #7154b9)",
              transform: "none",
              filter: "none",
              boxShadow: "0 6px 18px rgba(113, 84, 185, 0.5)",
            },
            _active: {
              bgGradient: "linear(135deg, #c091cf, #7154b9)",
              transform: "none",
              filter: "none",
            },
          },
        },
      },
    },

    Card: {
      baseStyle: {
        container: {
          borderRadius: "md",
          boxShadow: "sm",
          bg: "white",
          color: "silver.900",
        },
        header: { pb: 0 },
      },
    },

    Input: {
      variants: {
        outline: {
          field: {
            bg: "white",
            color: "silver.900",
            borderColor: "silver.200",
            _placeholder: { color: "silver.400" },
            _focus: {
              borderColor: "primary.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            },
          },
        },
      },
    },
    Select: {
      variants: {
        outline: {
          field: {
            bg: "white",
            color: "silver.900",
            borderColor: "silver.200",
            _placeholder: { color: "silver.400" },
            _focus: {
              borderColor: "primary.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
            },
          },
        },
      },
    },
    Textarea: {
      variants: {
        outline: {
          bg: "white",
          color: "silver.900",
          borderColor: "silver.200",
          _placeholder: { color: "silver.400" },
          _focus: {
            borderColor: "primary.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
          },
        },
      },
    },
    Link: {
      baseStyle: {
        color: "primary.600",
        _hover: { textDecoration: "none", color: "primary.700" },
      },
    },
    Table: {
      variants: {
        simple: {
          table: { bg: "white", color: "silver.900" },
          th: {
            borderBottom: "1px",
            borderColor: "silver.200",
            fontWeight: "semibold",
            p: 3,
            textTransform: "none",
            letterSpacing: "normal",
          },
          td: {
            borderBottom: "1px",
            borderColor: "silver.100",
            p: 3,
          },
        },
      },
    },
  },

  styles: {
    global: {
      body: {
        bg: "white",
        color: "silver.900",
      },
    },
  },
})

export default theme
